/**
 *  @internal Used for iterating over an object's keys. Any inherited or custom properties will be skipped.
 *
 *  > Note that order is not guaranteed!
 *
 *  Optionally the callback can be bound to some object/instance by providing it as the last argument.
 *  E.g.: `callback.call(thisArg, ...arguments);`
 *
 *  ```ts
 *
 *  interface SomeInterface {
 *      [key: string]: string
 *  }
 *
 *  const SomeObject = function (this: SomeInterface) {
 *
 *      this.value_a = 'test-value-a';
 *      this.value_b = 'test-value-b';
 *      this.value_c = 'test-value-c';
 *      this.value_d = 'test-value-d';
 *
 *  } as any as { new(): SomeInterface, currentIndex: number, increment: () => number };
 *
 *  SomeObject.prototype.currentIndex = 0;
 *
 *  SomeObject.prototype.increment = function (): number {
 *
 *      SomeObject.prototype.currentIndex++;
 *
 *      return SomeObject.prototype.currentIndex;
 *
 *  };
 *
 *  const someObject = new SomeObject();
 *
 *  forOwn(someObject, function (this: typeof SomeObject, currentValue: string, currentKey: string, fullObject: SomeInterface) {
 *
 *      if (this.currentIndex > 1) {
 *
 *          // Returning false (not falsy) will abort the iteration, any remaining key-value pairs will not be visited
 *          return false;
 *
 *      }
 *
 *      console.log(currentKey, currentValue);
 *
 *      this.increment();
 *
 *      // Returning true or void will continue the iteration.
 *      return; //return true;
 *
 *  }, someObject);
 *
 *  ```
 *
 *  @internal
 *
 */
export default function forOwn(obj, cb, thisArg) {
    for (const key in obj) {
        // Ignore any prototype properties
        if (!Object.prototype.hasOwnProperty.call(obj, key)) {
            continue;
        }
        // Break if callback returns false
        if (false === cb.call(thisArg, obj[key], key, obj)) {
            break;
        }
    }
}
//# sourceMappingURL=forOwn.js.map